import { useCallback, useEffect, useState } from 'react';
import { DRAWER_TRANSITION_DURATION_MS } from '../constants/drawerConstants';

export interface DrawerEditSlide {
  isEditing: boolean;
  isEditSliding: boolean;
  startEdit: () => void;
  endEdit: () => void;
  close: () => void;
}

/**
 * The view-to-edit slide inside a drawer, and the Escape key that unwinds it.
 *
 * `isEditing` keeps the edit card mounted; `isEditSliding` drives the slide, one
 * frame later, so the transition has a starting position to animate from. Ending
 * an edit reverses that: the slide goes first and the card unmounts once it has
 * finished.
 *
 * Escape unwinds one layer at a time — out of the edit view if it is open,
 * otherwise out of the drawer.
 */
export function useDrawerEditSlide(isOpen: boolean, onClose: () => void): DrawerEditSlide {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditSliding, setIsEditSliding] = useState(false);

  // The drawer is never unmounted by its parent, so closing it has to clear the
  // edit state by hand or it reopens mid-edit. Done during render rather than in
  // an effect: an effect would show one frame of the stale edit view first.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen && !isOpen) {
    setPrevIsOpen(false);
    setIsEditing(false);
    setIsEditSliding(false);
  } else if (!prevIsOpen && isOpen) {
    setPrevIsOpen(true);
  }

  const startEdit = useCallback(() => {
    setIsEditing(true);
    requestAnimationFrame(() => setIsEditSliding(true));
  }, []);

  const endEdit = useCallback(() => {
    setIsEditSliding(false);
    setTimeout(() => setIsEditing(false), DRAWER_TRANSITION_DURATION_MS);
  }, []);

  const close = useCallback(() => {
    setIsEditing(false);
    setIsEditSliding(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          endEdit();
        } else {
          close();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close, isEditing, endEdit]);

  return { isEditing, isEditSliding, startEdit, endEdit, close };
}
